import { Routes } from '@angular/router';
import { AppShell } from '@shells/app/app-shell/app-shell';
import { PublicShell } from '@shells/public/public-shell/public-shell';
import { AUTH_ROUTES, JETCRETE_AUTH_ROUTES } from '@features/auth/auth.routes';
import { authenticatedMatch, authGuard, publicGuard } from '@core/auth/auth.guard';
import { JETCRETE_BRAND, SCI_BRAND } from '@shells/public/public-brand';
import { AUTHENTICATED_EXIT, PUBLIC_EXIT } from '@features/not-found/not-found-exit';
import { ResourcePermissionsEnum } from '@core/permissions/permissions.enum';
import { permissionsGuard } from '@core/permissions/permissions.guard';
import { PROJECT_NAME } from '@shared/global/globals';
import { onlySuperadminGuard } from '@core/permissions/only-superadmin-guard';

const notFoundPage = () => import('@features/not-found/not-found-page').then((m) => m.NotFoundPage);

export const routes: Routes = [
  // Both guards redirect, so this lands on the shell the session allows.
  { path: '', pathMatch: 'full', redirectTo: 'project-tracker' },
  // One public shell branch per brand: the shell and the login page both read
  // `brand` from route data, so a white label is configuration, not a copy.
  {
    path: '',
    component: PublicShell,
    canActivate: [publicGuard],
    data: { brand: SCI_BRAND },
    children: AUTH_ROUTES,
  },
  {
    path: '',
    component: PublicShell,
    canActivate: [publicGuard],
    data: { brand: JETCRETE_BRAND },
    children: JETCRETE_AUTH_ROUTES,
  },
  {
    path: '',
    component: AppShell,
    canActivate: [authGuard],
    children: [
      {
        path: 'project-tracker',
        loadComponent: () => import('@features/project-tracker/project-tracker').then((m) => m.ProjectTracker),
        title: `Project Tracker - ${PROJECT_NAME}`,
      },
      {
        path: 'ui-kit',
        loadChildren: () => import('@features/ui-kit/ui-kit.routes').then((m) => m.UI_KIT_ROUTES),
        title: `UI Kit - ${PROJECT_NAME}`,
      },
      {
        // Where `permissionsGuard` denies to. Inside the app shell and behind
        // `authGuard`, since a denial is only reachable once signed in.
        path: 'forbidden',
        loadComponent: () => import('@features/forbidden/forbidden-page').then((m) => m.ForbiddenPage),
        title: `Access denied - ${PROJECT_NAME}`,
      },
      {
        path: 'project-admin',
        data: { menuResource: ResourcePermissionsEnum.Project },
        canMatch: [permissionsGuard],
        loadChildren: () => import('@features/project-admin/project-admin.routes').then((m) => m.PROJECT_ADMIN_ROUTES)
      },
      {
        path: 'admin',
        canMatch: [onlySuperadminGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('@features/admin/admin.routes').then((m) => m.ADMIN_ROUTES)
      }
    ]
  },
  // Unmatched URLs render a 404 in whichever shell the session allows. These
  // branches stay last so every real route above wins the match first.
  {
    path: '',
    component: AppShell,
    canMatch: [authenticatedMatch],
    children: [
      {
        path: '**',
        loadComponent: notFoundPage,
        title: `Page not found - ${PROJECT_NAME}`,
        data: { exit: AUTHENTICATED_EXIT },
      },
    ],
  },
  {
    path: '',
    component: PublicShell,
    data: { brand: SCI_BRAND },
    children: [
      {
        path: 'unauthorized',
        loadComponent: () => import('@features/unauthorized/unauthorized-page').then((m) => m.UnauthorizedPage),
        title: `Session expired - ${PROJECT_NAME}`,
      },
      {
        path: '**',
        loadComponent: notFoundPage,
        title: `Page not found - ${PROJECT_NAME}`,
        data: { exit: PUBLIC_EXIT },
      },
    ],
  }
];
